import { IS_FILE_COMPRESSION_ENABLED } from 'src/config';

export type CompressionStrategy = 'zstd_max' | 'zstd_try' | 'skip';

export const UPLOAD_STRATEGY: Record<string, CompressionStrategy> = {
  'text/csv': 'zstd_max',
  'text/plain': 'zstd_max',
  'text/rtf': 'zstd_max',
  'application/rtf': 'zstd_max',
  'application/json': 'zstd_max',
  'application/xml': 'zstd_max',
  'text/xml': 'zstd_max',
  'text/html': 'zstd_max',
  'application/msword': 'zstd_try',
  'application/vnd.ms-excel': 'zstd_try',
  'application/vnd.ms-powerpoint': 'zstd_try',
  'audio/wav': 'skip',
  'application/pdf': 'zstd_try',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'zstd_try',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'zstd_try',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'zstd_try',
  'application/vnd.oasis.opendocument.text': 'zstd_try',
  'application/vnd.oasis.opendocument.spreadsheet': 'zstd_try',
  'application/vnd.oasis.opendocument.presentation': 'zstd_try',
  'image/jpeg': 'skip',
  'image/png': 'skip',
  'image/gif': 'skip',
  'image/webp': 'skip',
  'image/svg+xml': 'skip',
  'image/bmp': 'skip',
  'image/tiff': 'skip',
  'image/avif': 'skip',
  'audio/mpeg': 'skip',
  'audio/mp4': 'skip',
  'audio/x-m4a': 'skip',
  'audio/ogg': 'skip',
  'audio/flac': 'skip',
  'audio/webm': 'skip',
  'video/mp4': 'skip',
  'video/mpeg': 'skip',
  'video/webm': 'skip',
  'video/ogg': 'skip',
  'video/quicktime': 'skip'
};

const ZSTD_MAGIC = [0x28, 0xb5, 0x2f, 0xfd] as const;

export function isZstd(data: Uint8Array): boolean {
  return (
    data.length >= 4 &&
    data[0] === ZSTD_MAGIC[0] &&
    data[1] === ZSTD_MAGIC[1] &&
    data[2] === ZSTD_MAGIC[2] &&
    data[3] === ZSTD_MAGIC[3]
  );
}

export interface ProcessedFile {
  buffer: ArrayBuffer | null;
  wasCompressed: boolean;
  noop: boolean; // true = no processing needed, caller should use the original File
}

interface PendingJob {
  resolve: (result: ProcessedFile) => void;
}

interface WorkerResponse {
  id: number;
  buffer: ArrayBuffer | null;
  wasCompressed: boolean;
}

let worker: Worker | null = null;
const pending = new Map<number, PendingJob>();
let nextId = 0;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('./compressEncryptWorker.ts', import.meta.url),
      { type: 'module' }
    );

    worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
      const job = pending.get(data.id);
      if (!job) return;
      pending.delete(data.id);
      job.resolve({ buffer: data.buffer, wasCompressed: data.wasCompressed, noop: false });
    };

    worker.onerror = (e) => {
      console.error('[compressEncryptPool] worker error:', e);
      pending.forEach((job) => job.resolve({ buffer: null, wasCompressed: false, noop: false }));
      pending.clear();
      worker = null;
    };
  }
  return worker;
}

export const compressEncryptPool = {
  process(file: File, key: any, isEncryptionEnabled: boolean): Promise<ProcessedFile> {
    const noop: ProcessedFile = { buffer: null, wasCompressed: false, noop: true };

    if (file.size === 0) return Promise.resolve(noop);

    const strategy: CompressionStrategy = IS_FILE_COMPRESSION_ENABLED
      ? (UPLOAD_STRATEGY[file.type] ?? 'skip')
      : 'skip';

    // Normalise undefined → null so the worker's `key !== null && key !== undefined`
    // guard never silently skips encryption while returning a non-null buffer.
    const effectiveKey = isEncryptionEnabled ? (key ?? null) : null;
    const needsWork = strategy !== 'skip' || effectiveKey !== null;

    // Nothing to do — caller uses the original File unchanged.
    if (!needsWork) return Promise.resolve(noop);

    return new Promise((resolve) => {
      const id = nextId++;
      pending.set(id, { resolve });
      // File is structure-cloned (Blob handle only, bytes read inside worker).
      getWorker().postMessage({ id, file, key: effectiveKey, strategy });
    });
  },

  terminate() {
    worker?.terminate();
    worker = null;
    pending.clear();
  }
};
